// TEMPORARY MOCK ADAPTER for Management Guidelines (PRD 07).
// In-memory only — state lives in this module's `store` array and resets on
// hard reload. Mutations resolve with ~300 ms simulated latency. Errors mimic
// the backend envelope (ManagementGuidelineApiError). Backend switch:
// VITE_USE_MOCK_API=false flips to the real HTTP adapter
// (management-guidelines.adapter.ts).
//
// PRD 04 / Slice 14 — `getProTree()` and the create/update PID derivation now
// consume the PRO Item Design feature as the single source of truth (the tree
// is mapped to the ProTree wire shape via `domainsToProTree`). The lingering
// `BOOTSTRAP_TOPICS` table below covers two seed-time concerns only:
//   1) deriving (category, subArea) for legacy seed guidelines whose
//      `linkedItem` strings don't yet exist in pro-item-design's compact seed;
//   2) keeping module init synchronous (pro-item-design's API is async).
// Once pro-item-design's mock seed grows to cover all referenced items, the
// bootstrap can be removed and `store` can lazy-load via the async API.

import { proItemDesignApi } from '@/features/pro-item-design/api/pro-item-design.adapter';

import {
  GUIDELINE_GRADES,
  ManagementGuidelineApiError,
  type ContentBlock,
  type CreateGuidelineInput,
  type DeleteGuidelineResult,
  type GuidelineGrade,
  type GuidelineStatus,
  type ManagementGuideline,
  type ManagementGuidelineApi,
  type ManagementGuidelineDetail,
  type ProTopicNode,
  type ProTree,
  type UpdateGuidelineInput,
} from '../types/management-guidelines.types';
import { domainsToProTree } from '../utils/topic-tree';

const LATENCY_MS = 300;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ── Seed-time cascade backfill (legacy support) ──────────────────────────────
// Static (category, subArea) lookup table consulted by `deriveCascade` when
// hydrating the seed guidelines. Live consumers (getProTree, pidsByArea) bypass
// this and derive from pro-item-design instead.
const BOOTSTRAP_TOPICS: ProTopicNode[] = [
  { id: 34, category: '정서·인지 증상 관리', subArea: '정서 증상', items: [{ name: '지속적인 우울감' }, { name: '자살사고' }] },
  { id: 33, category: '정서·인지 증상 관리', subArea: '정서 증상', items: [{ name: '불안의 급격한 악화' }] },
  { id: 32, category: '정서·인지 증상 관리', subArea: '약물 관련', items: [{ name: '복약 상태' }] },
  { id: 31, category: '정서·인지 증상 관리', subArea: '약물 관련', items: [{ name: '약물 부작용 체감도' }] },
  { id: 30, category: '정서·인지 증상 관리', subArea: '수면·피로', items: [{ name: '불면·과수면' }] },
  { id: 29, category: '신체·생활습관 요인 관리', subArea: '건강행태', items: [{ name: '신체활동 악화' }] },
  { id: 28, category: '신체·생활습관 요인 관리', subArea: '건강행태', items: [{ name: '고위험 음주 여부' }] },
  { id: 27, category: '신체·생활습관 요인 관리', subArea: '일상생활 구조 유지', items: [{ name: '규칙적인 식사 유지' }] },
  { id: 26, category: '신체·생활습관 요인 관리', subArea: '일상생활 구조 유지', items: [{ name: '규칙적인 수면 유지' }] },
  { id: 25, category: '신체·생활습관 요인 관리', subArea: '관계 회복', items: [{ name: '사회적 접촉 회피' }] },
  { id: 24, category: '신체·생활습관 요인 관리', subArea: '역할 수행', items: [{ name: '일상생활수행능력 유지' }] },
  { id: 23, category: '사회·환경적 지원', subArea: '의료자원', items: [{ name: '상담 창구 확보' }] },
  { id: 22, category: '사회·환경적 지원', subArea: '복지자원', items: [{ name: '사회보장 서비스 접근성' }] },
  { id: 21, category: '사회·환경적 지원', subArea: '지역사회 사례관리', items: [{ name: '지역사회 사례관리 연계 유지' }] },
  { id: 20, category: '사회·환경적 지원', subArea: '디지털 자기기록', items: [{ name: '디지털 자기기록 지속성' }] },
  { id: 19, category: '자기관리 및 가족·자원 연계', subArea: '질병 인식', items: [{ name: '환자 본인의 질병 및 증상 이해도' }, { name: '치료 접근 장벽 인식' }] },
  { id: 18, category: '자기관리 및 가족·자원 연계', subArea: '보호자 이해도·돌봄 역량', items: [{ name: '보호자 질병 이해도' }, { name: '응급 상황 대처 방법 인지' }] },
  { id: 17, category: '자기관리 및 가족·자원 연계', subArea: '돌봄 부담 및 안전', items: [{ name: '돌봄 부담 체감도' }, { name: '위기 시 비상 연락' }] },
];

// ── Seed: guidelines (prototype MockData.managementGuidelines) ──────────────
// Seeded `pids` are intentionally kept verbatim from the prototype (random
// P-### values) — the prototype itself shows these in the list and only
// derives PIDs from Category inside the edit panel. `category`/`subArea` are
// resolved on first access from `BOOTSTRAP_TOPICS` (see deriveCascade).
interface SeedGuideline {
  gid: string;
  name: string;
  grade: GuidelineGrade;
  linkedItem: string;
  pids: string[];
  status: GuidelineStatus;
  deployedAt: string | null;
}

const seed: SeedGuideline[] = [
  { gid: 'G-001', name: '부작용 모니터링(오심, 체중 증가, 성기능 장애 등) 관리 가이드 5차', grade: 'G1', linkedItem: '복약 상태', pids: [], status: 'DRAFT', deployedAt: null },
  { gid: 'G-002', name: '흥미 상실 관리 가이드 6차', grade: 'G2', linkedItem: '지속적인 우울감', pids: [], status: 'DRAFT', deployedAt: null },
  { gid: 'G-003', name: '고위험 음주 여부 관리 가이드 2차', grade: 'G3', linkedItem: '고위험 음주 여부', pids: [], status: 'PUBLISHED', deployedAt: '2024.07.14' },
  { gid: 'G-004', name: '응급 상황 대처 방법 인지 관리 가이드 6차', grade: 'G1', linkedItem: '응급 상황 대처 방법 인지', pids: ['P-788', 'P-763', 'P-692'], status: 'PUBLISHED', deployedAt: '2024.07.19' },
  { gid: 'G-005', name: '현기증으로 인한 지속적 에너지 저하 관리 가이드 9차', grade: 'G2', linkedItem: '불면·과수면', pids: ['P-575'], status: 'PUBLISHED', deployedAt: '2024.03.11' },
  { gid: 'G-006', name: '규칙적인 식사 유지 관리 가이드 10차', grade: 'G3', linkedItem: '규칙적인 식사 유지', pids: [], status: 'DRAFT', deployedAt: null },
  { gid: 'G-007', name: '여가·일 구조 유지 관리 가이드 6차', grade: 'G1', linkedItem: '일상생활수행능력 유지', pids: ['P-642', 'P-119'], status: 'PUBLISHED', deployedAt: '2024.02.09' },
  { gid: 'G-008', name: '자살사고(자살생각, 자살시도) 관리 가이드 10차', grade: 'G2', linkedItem: '자살사고', pids: ['P-915'], status: 'PUBLISHED', deployedAt: '2024.03.28' },
  { gid: 'G-009', name: '복약 상태 관리 가이드 6차', grade: 'G3', linkedItem: '복약 상태', pids: ['P-491', 'P-766', 'P-901'], status: 'PUBLISHED', deployedAt: '2024.08.04' },
  { gid: 'G-010', name: '고위험 음주 여부 관리 가이드 5차', grade: 'G1', linkedItem: '고위험 음주 여부', pids: ['P-136', 'P-706', 'P-432'], status: 'PUBLISHED', deployedAt: '2024.02.27' },
  { gid: 'G-011', name: '식욕 변화로 신체 불편 관리 가이드 5차', grade: 'G2', linkedItem: '오심·구토로 신체 불편', pids: ['P-113'], status: 'PUBLISHED', deployedAt: '2024.03.17' },
  { gid: 'G-012', name: '상담 창구 확보 관리 가이드 8차', grade: 'G3', linkedItem: '상담 창구 확보', pids: ['P-227', 'P-500'], status: 'PUBLISHED', deployedAt: '2024.04.14' },
  { gid: 'G-013', name: '자기 낙인 경험 관리 가이드 5차', grade: 'G1', linkedItem: '자기 낙인 경험', pids: ['P-334'], status: 'PUBLISHED', deployedAt: '2024.05.06' },
  { gid: 'G-014', name: '정신질환에 대한 사회적 낙인과 차별 인식 관리 가이드 5차', grade: 'G2', linkedItem: '정신질환에 대한 사회적 낙인과 차별 인식', pids: ['P-534'], status: 'PUBLISHED', deployedAt: '2024.08.12' },
  { gid: 'G-015', name: '집중력 저하 관리 가이드 6차', grade: 'G3', linkedItem: '집중력 저하', pids: [], status: 'DRAFT', deployedAt: null },
  { gid: 'G-016', name: '치료 접근을 가로막는 장벽(경제적 부담 등) 인식 관리 가이드 9차', grade: 'G1', linkedItem: '치료 접근 장벽 인식', pids: ['P-999'], status: 'PUBLISHED', deployedAt: '2024.02.21' },
  { gid: 'G-017', name: '지속적인 우울감 관리 가이드 7차', grade: 'G2', linkedItem: '지속적인 우울감', pids: ['P-482', 'P-681'], status: 'PUBLISHED', deployedAt: '2024.10.09' },
  { gid: 'G-018', name: '판단력 저하 관리 가이드 7차', grade: 'G3', linkedItem: '판단력 저하', pids: ['P-378', 'P-596'], status: 'PUBLISHED', deployedAt: '2024.09.14' },
  { gid: 'G-019', name: '규칙적인 수면 유지 관리 가이드 9차', grade: 'G1', linkedItem: '규칙적인 수면 유지', pids: ['P-781'], status: 'PUBLISHED', deployedAt: '2024.10.25' },
  { gid: 'G-020', name: '신체활동(걷기실천, 중등도 이상 신체활동) 약화 관리 가이드 9차', grade: 'G2', linkedItem: '신체활동 악화', pids: ['P-464', 'P-859'], status: 'PUBLISHED', deployedAt: '2024.08.14' },
];

// Detail records carry the full block array; list records keep only the row
// fields. Both share the same row shape so updates flow through one helper.
interface DetailRecord extends SeedGuideline {
  category: string;
  subArea: string;
  // SHCT-220 — the bound leaf's unique id (see deriveLinkedItemId). Null when the seed's linked item
  // is outside the bootstrap subset.
  linkedItemId: number | null;
  blocks: ContentBlock[];
}

// Mirrors prototype `getTopicPathByItem`: find which topic node contains the
// given item name; returns empty strings when the linked item isn't in the
// bootstrap (some seed entries reference items outside the bootstrap subset).
// Synchronous because the seed array is initialized at module load.
function deriveCascade(itemName: string): { category: string; subArea: string } {
  for (const topic of BOOTSTRAP_TOPICS) {
    if (topic.items.some((item) => item.name === itemName)) {
      return { category: topic.category, subArea: topic.subArea };
    }
  }
  return { category: '', subArea: '' };
}

// SHCT-220 — the real backend carries the bound leaf's id on the row itself. The
// mock has only the name, so it resolves the id the same way `deriveCascade`
// resolves the cascade: by looking the name up in the PRO-item seed. Kept as a
// literal table (not an async tree read) to preserve this module's synchronous
// init; the ids mirror pro-item-design.mock's leaf items exactly.
//
// Names outside that compact seed map to null — the same rows `deriveCascade`
// already returns empty strings for. They match no PRO item either way, so the
// switch from name-keyed to id-keyed lookup leaves their behaviour unchanged.
const MOCK_LEAF_ITEM_IDS: Record<string, number> = {
  '지속적인 우울감': 111,
  '자살 관련 행동': 112,
  '복약 상태': 121,
  '불면·과수면': 131,
  '신체활동 악화': 211,
  '고위험 음주': 212,
};

function deriveLinkedItemId(itemName: string): number | null {
  return MOCK_LEAF_ITEM_IDS[itemName] ?? null;
}

let store: DetailRecord[] = seed.map((row) => ({
  ...row,
  ...deriveCascade(row.linkedItem),
  linkedItemId: deriveLinkedItemId(row.linkedItem),
  blocks: [],
}));

// ── Validation helpers (PRD §4.2) ───────────────────────────────────────────
function validateName(name: string | undefined): string {
  const cleaned = (name ?? '').trim();
  if (!cleaned) {
    throw new ManagementGuidelineApiError(
      'NAME_REQUIRED',
      '관리지침명을 입력해주세요.',
      { fields: { name: '관리지침명을 입력해주세요.' } },
    );
  }
  if (cleaned.length > 200) {
    throw new ManagementGuidelineApiError(
      'NAME_TOO_LONG',
      '관리지침명은 200자 이내로 입력해주세요.',
      { fields: { name: '관리지침명은 200자 이내로 입력해주세요.' } },
    );
  }
  return cleaned;
}

function validateCascade(category: string | undefined, subArea: string | undefined, item: string | undefined): {
  category: string;
  subArea: string;
  linkedItem: string;
} {
  const c = (category ?? '').trim();
  const s = (subArea ?? '').trim();
  const i = (item ?? '').trim();
  if (!c) {
    throw new ManagementGuidelineApiError('CATEGORY_REQUIRED', '영역을 선택해주세요.', {
      fields: { category: '영역을 선택해주세요.' },
    });
  }
  if (!s) {
    throw new ManagementGuidelineApiError('SUB_AREA_REQUIRED', '하위영역을 선택해주세요.', {
      fields: { subArea: '하위영역을 선택해주세요.' },
    });
  }
  if (!i) {
    throw new ManagementGuidelineApiError(
      'LINKED_ITEM_REQUIRED',
      '연계할 측정 항목을 선택해주세요.',
      { fields: { linkedItem: '연계할 측정 항목을 선택해주세요.' } },
    );
  }
  return { category: c, subArea: s, linkedItem: i };
}

function validateGrade(grade: GuidelineGrade | undefined): GuidelineGrade {
  if (!grade || !GUIDELINE_GRADES.includes(grade)) {
    throw new ManagementGuidelineApiError('GRADE_REQUIRED', 'Grade를 선택해주세요.', {
      fields: { grade: 'Grade를 선택해주세요.' },
    });
  }
  return grade;
}

/**
 * SHCT-777 — PRD 07 §3: "관리지침 ↔ PRO 항목 1:1 매칭 … 하나의 관리지침은 하나의 항목·단계(Grade)에만
 * 매칭된다(영역/하위영역/항목 + Grade 조합으로 결정)". So that four-part combination is the key, and a
 * second guideline on it is a conflict, not a variant: PRD 04 §9 shows the linked guideline as
 * "1:1로 연결된 1개를 읽기전용", which has no answer once two rows claim the same slot.
 *
 * `selfGid` is the row being edited, excluded so re-saving it is not a conflict with itself.
 *
 * The REAL check belongs to the server — only it sees every row and only it can make the check atomic
 * against a concurrent save. This mirrors it so the screen behaves the same with mocks on.
 */
function ensureCombinationFree(
  cascade: { category: string; subArea: string; linkedItem: string },
  grade: GuidelineGrade,
  selfGid: string | null,
): void {
  const clash = store.find(
    (g) =>
      g.gid !== selfGid &&
      g.category === cascade.category &&
      g.subArea === cascade.subArea &&
      g.linkedItem === cascade.linkedItem &&
      g.grade === grade,
  );
  if (clash) {
    throw new ManagementGuidelineApiError(
      'GUIDELINE_DUPLICATE',
      '이미 등록된 조합입니다.',
      { meta: { gid: clash.gid, name: clash.name } },
    );
  }
}

// PRD §3.4 — PUBLISHED → DRAFT is forbidden. PUBLISHED → PUBLISHED is allowed
// and refreshes deployedAt.
function ensureStatusTransition(prev: GuidelineStatus, next: GuidelineStatus): void {
  if (prev === 'PUBLISHED' && next === 'DRAFT') {
    throw new ManagementGuidelineApiError(
      'STATUS_TRANSITION_FORBIDDEN',
      '저장된 관리지침은 임시저장 상태로 되돌릴 수 없습니다.',
    );
  }
}

function nextGid(): string {
  // PRD §4.4 — system auto-assigned, 'G-###' zero-padded to 3 digits.
  const maxNum = store.reduce((max, row) => {
    const n = Number.parseInt(row.gid.replace('G-', ''), 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `G-${String(maxNum + 1).padStart(3, '0')}`;
}

// PRD 04 — derives the PID chips for a given Category by walking the LIVE
// pro-item-design tree. Used at create/update time so a guideline's PID set
// always reflects the current Items registered under that Category. Areas
// not present in the tree yield an empty set.
async function pidsByArea(area: string): Promise<string[]> {
  if (!area) return [];
  const tree = await proItemDesignApi.getTree();
  const seen = new Set<string>();
  for (const domain of tree) {
    if (domain.name !== area) continue;
    for (const sa of domain.subAreas) {
      for (const it of sa.items) seen.add(it.pid);
    }
  }
  return Array.from(seen);
}

// Today as YYYY.MM.DD in KST. PRD §4.4.
function todayKst(): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // 'en-CA' yields YYYY-MM-DD; convert to dots to match the spec.
  return fmt.format(new Date()).replace(/-/g, '.');
}

function toListRow(record: DetailRecord): ManagementGuideline {
  return {
    gid: record.gid,
    name: record.name,
    grade: record.grade,
    linkedItem: record.linkedItem,
    linkedItemId: deriveLinkedItemId(record.linkedItem),
    pids: record.pids.slice(),
    status: record.status,
    deployedAt: record.deployedAt,
  };
}

function toDetail(record: DetailRecord): ManagementGuidelineDetail {
  return {
    ...toListRow(record),
    category: record.category,
    subArea: record.subArea,
    blocks: clone(record.blocks),
  };
}

function findOrThrow(gid: string): DetailRecord {
  const record = store.find((r) => r.gid === gid);
  if (!record) {
    throw new ManagementGuidelineApiError('NOT_FOUND', '관리지침을 찾을 수 없습니다.');
  }
  return record;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result as string);

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

export const managementGuidelinesMockApi: ManagementGuidelineApi = {
  async list(): Promise<ManagementGuideline[]> {
    await delay();
    return clone(store.map(toListRow));
  },

  async get(gid: string): Promise<ManagementGuidelineDetail> {
    await delay();
    return toDetail(findOrThrow(gid));
  },

  async create(input: CreateGuidelineInput): Promise<ManagementGuidelineDetail> {
    await delay();
    const name = validateName(input.name);
    const cascade = validateCascade(input.category, input.subArea, input.linkedItem);
    const grade = validateGrade(input.grade);
    ensureCombinationFree(cascade, grade, null);

    const record: DetailRecord = {
      gid: nextGid(),
      name,
      grade,
      ...cascade,
      linkedItemId: deriveLinkedItemId(cascade.linkedItem),
      pids: await pidsByArea(cascade.category),
      blocks: clone(input.blocks ?? []),
      status: input.status,
      deployedAt: input.status === 'PUBLISHED' ? todayKst() : null,
    };
    store = [...store, record];
    return toDetail(record);
  },

  async update(gid: string, input: UpdateGuidelineInput): Promise<ManagementGuidelineDetail> {
    await delay();
    const current = findOrThrow(gid);
    const nextStatus = input.status ?? current.status;
    ensureStatusTransition(current.status, nextStatus);

    const name = input.name !== undefined ? validateName(input.name) : current.name;
    const cascade =
      input.category !== undefined || input.subArea !== undefined || input.linkedItem !== undefined
        ? validateCascade(
            input.category ?? current.category,
            input.subArea ?? current.subArea,
            input.linkedItem ?? current.linkedItem,
          )
        : { category: current.category, subArea: current.subArea, linkedItem: current.linkedItem };
    const grade = input.grade !== undefined ? validateGrade(input.grade) : current.grade;
    // SHCT-777 — an edit reaches the same combination as easily as a create does (change the 항목, or
    // the Grade, onto one already taken), so the 1:1 rule has to hold here too.
    ensureCombinationFree(cascade, grade, current.gid);

    const updated: DetailRecord = {
      ...current,
      name,
      grade,
      ...cascade,
      // Re-derived, not carried over from `current`: an edit may re-point the guideline at a different
      // item, and a stale id would then disagree with the name spread in from `cascade` above.
      linkedItemId: deriveLinkedItemId(cascade.linkedItem),
      pids: await pidsByArea(cascade.category),
      blocks: input.blocks !== undefined ? clone(input.blocks) : current.blocks,
      status: nextStatus,
      // PUBLISHED save (initial or re-save) always refreshes the date; DRAFT
      // leaves it null.
      deployedAt: nextStatus === 'PUBLISHED' ? todayKst() : null,
    };
    store = store.map((row) => (row.gid === gid ? updated : row));
    return toDetail(updated);
  },

  async remove(gid: string): Promise<DeleteGuidelineResult> {
    await delay();
    const target = findOrThrow(gid);
    const unlinkedPidCount = target.pids.length;
    store = store.filter((row) => row.gid !== gid);
    return { unlinkedPidCount };
  },

  // PRD §3.2 — the single whole-tree read backing BOTH the cascade select and
  // the read-only PID chips. Sourced from the PRO Item Design feature (single
  // source of truth) and mapped into the ProTree wire shape. No simulated
  // `delay()` because the inner `getTree()` already pays one.
  async getProTree(): Promise<ProTree> {
    const tree = await proItemDesignApi.getTree();
    return domainsToProTree(tree);
  },

  // No real storage in mock mode — return a base64 dataUrl so the editor preview
  // works offline. The real adapter uploads to GCS and returns a CDN URL instead.
  async uploadMedia(file: File): Promise<string> {
    await delay();
    return fileToDataUrl(file);
  },
};
